import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Clock, 
  Plus,
  Trash2,
  Calendar,
  Phone,
  MessageSquare,
  Video
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sessionTypes = ['phone', 'chat', 'video'];
const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30'
];

export default function AdminSlots() {
  const { currentTheme } = useTheme();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    session_type: ''
  });

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/coaching/slots`);
      setSlots(response.data || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const data = new FormData();
      data.append('date', formData.date);
      data.append('time', formData.time);
      data.append('session_type', formData.session_type);

      await axios.post(`${API}/admin/slots`, data, { withCredentials: true });
      toast.success('Slot created successfully!');
      setShowDialog(false);
      setFormData({ date: '', time: '', session_type: '' });
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create slot');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) return;

    try {
      await axios.delete(`${API}/admin/slots/${slotId}`, { withCredentials: true });
      toast.success('Slot deleted');
      fetchSlots();
    } catch (error) {
      toast.error('Failed to delete slot');
    }
  };

  const getSessionIcon = (type) => {
    switch (type) {
      case 'phone': return Phone;
      case 'chat': return MessageSquare;
      case 'video': return Video;
      default: return Phone;
    }
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  return (
    <AdminLayout title="Time Slots">
      <div className="flex justify-between items-center mb-6">
        <p className="font-body opacity-70">
          Create available time slots for coaching sessions.
        </p>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: currentTheme.primary }} data-testid="add-slot-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Slot
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Create Time Slot</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="slot-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Time</Label>
                <Select 
                  value={formData.time} 
                  onValueChange={(v) => setFormData({ ...formData, time: v })}
                >
                  <SelectTrigger data-testid="slot-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Session Type</Label>
                <Select 
                  value={formData.session_type} 
                  onValueChange={(v) => setFormData({ ...formData, session_type: v })}
                >
                  <SelectTrigger data-testid="slot-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionTypes.map((type) => (
                      <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{ backgroundColor: currentTheme.primary }}
                disabled={creating}
                data-testid="create-slot-btn"
              >
                {creating ? 'Creating...' : 'Create Slot'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-48 rounded-xl" />
          ))}
        </div>
      ) : Object.keys(groupedSlots).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedSlots)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([date, dateSlots]) => (
              <div key={date}>
                <h3 className="font-display text-lg font-medium mb-4 flex items-center gap-2" style={{ color: currentTheme.foreground }}>
                  <Calendar className="w-5 h-5" />
                  {new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {dateSlots
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((slot) => {
                      const Icon = getSessionIcon(slot.session_type);
                      return (
                        <div
                          key={slot.slot_id}
                          className={`rounded-xl p-4 ${slot.is_booked ? 'opacity-50' : ''}`}
                          style={{ 
                            backgroundColor: 'white', 
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                            border: `1px solid ${currentTheme.border}`
                          }}
                          data-testid={`slot-card-${slot.slot_id}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="flex items-center gap-2 font-body font-medium">
                              <Clock className="w-4 h-4" style={{ color: currentTheme.primary }} />
                              {slot.time}
                            </span>
                            {slot.is_booked ? (
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs"
                                style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                              >
                                Booked
                              </span>
                            ) : (
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs"
                                style={{ backgroundColor: `${currentTheme.accent}20`, color: currentTheme.primary }}
                              >
                                Available
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1 text-sm capitalize opacity-70">
                              <Icon className="w-4 h-4" />
                              {slot.session_type}
                            </span>
                            {!slot.is_booked && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(slot.slot_id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-slot-${slot.slot_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
            No time slots
          </h3>
          <p className="font-body opacity-60">
            Create available slots for users to book coaching sessions.
          </p>
        </div>
      )}
    </AdminLayout>
  );
}
